import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, AlertTriangle, CalendarClock, Flag, CheckCircle2, ListTodo, ClipboardCheck, LayoutGrid, BarChart3, ArrowRight, Plus } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TaskCard from "@/components/TaskCard";
import TaskDialog from "@/components/TaskDialog";
import CompleteLinksDialog from "@/components/CompleteLinksDialog";
import { EmptyState } from "@/components/EmptyState";
import { MyTodos } from "@/components/MyTodos";
import { Button } from "@/components/ui/button";
import { todayIstIso } from "@/utils/date";
import { priorityRank, STATUS_META } from "@/constants/status";

const dayIso = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : null);
const statusOf = (t, uid) => {
  const slot = (t.assignee_details || []).find((a) => a.user_id === uid);
  return (t.assignees || []).length > 1 && slot ? slot.status : t.status;
};

function greeting() {
  const h = Number(new Date().toLocaleString("en-GB", { hour: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }));
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="glass rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone}`}><Icon size={18} /></div>
      <div>
        <div className="text-2xl font-display leading-none">{value}</div>
        <div className="text-xs text-white/55 mt-1">{label}</div>
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, count, tone, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={tone} />
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="text-xs text-white/50">{count}</span>
      </div>
      {children}
    </section>
  );
}

const QUICK = [
  { to: "/daily-log", label: "File daily log", icon: ClipboardCheck },
  { to: "/my-tasks", label: "My tasks", icon: ListTodo },
  { to: "/", label: "Team whiteboard", icon: LayoutGrid },
  { to: "/reports", label: "Reports & KRA", icon: BarChart3 },
];

export default function MyDay() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || !!user?.is_super_admin;
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [popup, setPopup] = useState(null);

  const load = async () => {
    const [t, u] = await Promise.all([api.get("/tasks"), api.get("/users")]);
    setTasks(t.data || []); setUsers(u.data || []); setLoading(false);
  };
  useEffect(() => { load().catch(() => setLoading(false)); }, []);

  const today = todayIstIso();
  const groups = useMemo(() => {
    const mine = tasks.filter((t) => (t.assignees || []).includes(user?.id) || t.assigned_to === user?.id);
    const open = mine.filter((t) => statusOf(t, user?.id) !== "complete");
    const byPrio = (a, b) => priorityRank(a) - priorityRank(b);
    const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
    return {
      overdue: open.filter((t) => t.deadline && dayIso(t.deadline) < today).sort(byPrio),
      today: open.filter((t) => {
        const s = t.start_date ? t.start_date.slice(0, 10) : dayIso(t.deadline);
        const e = dayIso(t.deadline) || s;
        return s && s <= today && e >= today;
      }).sort(byPrio),
      priorities: open.filter((t) => priorityRank(t) <= 1).sort(byPrio),
      open: open.length,
      doneWeek: mine.filter((t) => statusOf(t, user?.id) === "complete" && dayIso(t.deadline) >= weekAgo).length,
    };
  }, [tasks, user?.id, today]);

  const updateStatus = async (task, next, { hold_reason = "", incomplete_reason = "" } = {}) => {
    try {
      const payload = { status: next };
      if (next === "on_hold") payload.hold_reason = hold_reason;
      if (next === "incomplete") payload.incomplete_reason = incomplete_reason;
      const { data } = await api.patch(`/tasks/${task.id}`, payload);
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data : x)));
      toast.success(`“${task.title}” → ${STATUS_META[next]?.label || next}`);
      if (["complete", "carry_forward"].includes(next) && task.status !== next) setPopup({ ...data, __popup_mode: next });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Status update failed");
    }
  };
  const togglePriority = async (task, level) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { priority_level: level });
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data : x)));
    } catch { toast.error("Failed to update priority"); }
  };
  const openNew = () => { setEditing(null); setDialogOpen(true); };

  const grid = (list, empty, testid) => list.length === 0
    ? <EmptyState text={empty} actionLabel="Add task" onAction={openNew} testid={testid} />
    : (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {list.map((t) => (
          <TaskCard key={t.id} task={t} currentUser={user} users={users} isManager={isManager}
            onEdit={(x) => { setEditing(x); setDialogOpen(true); }} onStatusChange={updateStatus}
            onTogglePriority={isManager ? togglePriority : null} />
        ))}
      </div>
    );

  return (
    <div className="p-4 sm:p-6" data-testid="my-day-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline flex items-center gap-2"><Sun size={13} /> My Day</div>
          <h1 className="text-3xl sm:text-4xl font-display mt-1" data-testid="my-day-greeting">{greeting()}, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-white/55 mt-1">Here's what needs you today.</p>
        </div>
        <Button onClick={openNew} data-testid="my-day-new-task" className="bg-[#E11D2E] hover:bg-[#F03041] text-white rounded-full">
          <Plus size={15} className="mr-1" /> New task
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={CalendarClock} label="Due today" value={groups.today.length} tone="bg-blue-500/10 text-blue-600 dark:text-blue-400" testid="my-day-stat-today" />
        <Stat icon={AlertTriangle} label="Overdue" value={groups.overdue.length} tone="bg-red-500/10 text-red-600 dark:text-red-400" testid="my-day-stat-overdue" />
        <Stat icon={ListTodo} label="Open tasks" value={groups.open} tone="bg-slate-500/10 text-slate-600 dark:text-slate-300" testid="my-day-stat-open" />
        <Stat icon={CheckCircle2} label="Completed (last 7 days)" value={groups.doneWeek} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" testid="my-day-stat-done" />
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="my-day-quick-links">
        {QUICK.filter((q) => !(q.to === "/daily-log" && user?.is_super_admin)).map((q) => (
          <Link key={q.to} to={q.to} data-testid={`my-day-link-${q.to.replace(/\W+/g, "") || "whiteboard"}`}
            className="group inline-flex items-center gap-2 h-9 px-3 rounded-md border border-white/10 bg-surface text-sm text-white/80 hover:border-[#E11D2E]/40 hover:text-white transition-colors">
            <q.icon size={15} className="text-white/50 group-hover:text-[#E11D2E] transition-colors" /> {q.label}
            <ArrowRight size={13} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
          </Link>
        ))}
      </div>

      <MyTodos />

      {loading ? <div className="text-sm text-white/50">Loading your day…</div> : (
        <>
          {groups.overdue.length > 0 && (
            <Block icon={AlertTriangle} title="Overdue" count={groups.overdue.length} tone="text-red-500" testid="my-day-overdue">
              {grid(groups.overdue, "", "my-day-overdue-empty")}
            </Block>
          )}
          <Block icon={CalendarClock} title="Due today" count={groups.today.length} tone="text-blue-500" testid="my-day-today">
            {grid(groups.today, "Nothing scheduled for today.", "my-day-today-empty")}
          </Block>
          <Block icon={Flag} title="My priorities (Urgent & High)" count={groups.priorities.length} tone="text-orange-500" testid="my-day-priorities">
            {grid(groups.priorities, "No urgent or high-priority tasks. Nice.", "my-day-priorities-empty")}
          </Block>
        </>
      )}

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen} users={users} existing={editing}
        defaultAssignee={isManager ? null : user?.id} onSaved={() => { setDialogOpen(false); load(); }} />
      <CompleteLinksDialog open={!!popup} task={popup}
        onClose={(u) => { if (u) setTasks((p) => p.map((x) => (x.id === u.id ? u : x))); setPopup(null); }} />
    </div>
  );
}
